<template>
  <div class="iceberg-detection">
    <div class="header">
      <h2>冰山订单检测</h2>
      <div class="controls">
        <input
          v-model="stockCode"
          type="text"
          placeholder="股票代码 (如: 000001)"
          @keyup.enter="handleDetect"
          class="stock-input"
        />
        <select v-model.number="windowMinutes" class="time-select">
          <option :value="5">最近5分钟</option>
          <option :value="10">最近10分钟</option>
          <option :value="30">最近30分钟</option>
          <option :value="60">最近1小时</option>
          <option :value="1440">最近1天</option>
          <option :value="-1">自定义时间</option>
        </select>
        <template v-if="windowMinutes === -1">
          <input
            v-model="customStartTime"
            type="datetime-local"
            placeholder="开始时间"
            class="time-input"
          />
          <input
            v-model="customEndTime"
            type="datetime-local"
            placeholder="结束时间"
            class="time-input"
          />
        </template>
        <button @click="handleDetect" :disabled="loading" class="detect-btn">
          {{ loading ? '检测中...' : '检测冰山订单' }}
        </button>
        <button @click="checkDataStatus" :disabled="loading" class="status-btn">
          检查数据状态
        </button>
      </div>
    </div>

    <!-- 数据状态提示 -->
    <div v-if="dataStatus" class="data-status" :class="dataStatus.has_data ? 'has-data' : 'no-data'">
      <template v-if="dataStatus.has_data">
        <i class="el-icon-success"></i>
        <span>{{ dataStatus.message }}</span>
        <span v-if="dataStatus.total_count" class="detail">
          (共{{ dataStatus.total_count }}条, {{ dataStatus.days_count }}天数据)
        </span>
      </template>
      <template v-else>
        <i class="el-icon-warning"></i>
        <span>{{ dataStatus.message }}</span>
        <div v-if="dataStatus.hint" class="hint">{{ dataStatus.hint }}</div>
      </template>
    </div>

    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

    <div v-if="insufficientDataWarning" class="warning-message">
      <p><i class="el-icon-warning-outline"></i> 数据不足</p>
      <p>{{ insufficientDataWarning.message }}</p>
      <p class="hint">建议：先访问 <a href="#/realtime-quote" target="_blank">实时行情页面</a> 连接WebSocket获取数据，等待3-5分钟后重试</p>
    </div>

    <div v-if="detections.length > 0" class="results-section">
      <h3>检测结果 ({{ detections.length }}个疑似冰山订单)</h3>
      <div class="detection-table">
        <table>
          <thead>
            <tr>
              <th>方向</th>
              <th>价格</th>
              <th>寿命(秒)</th>
              <th>平均量</th>
              <th>量变异(CV)</th>
              <th>价格稳定</th>
              <th>置信度</th>
              <th>检测模式</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(det, index) in detections" :key="index">
              <td :class="det.side_en">{{ det.side }}</td>
              <td>{{ det.price.toFixed(2) }}</td>
              <td>{{ det.lifetime_seconds }}</td>
              <td>{{ det.avg_volume.toFixed(0) }}</td>
              <td>{{ det.volume_cv }}</td>
              <td>{{ det.price_stability }}%</td>
              <td :class="getConfidenceClass(det.confidence)">{{ det.confidence }}%</td>
              <td>{{ det.detection_modes.join(' + ') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="chartData.length > 0" class="charts">
      <div class="chart-row">
        <div class="chart-container">
          <h4>买一量变化</h4>
          <div ref="buy1Chart" class="chart"></div>
        </div>
        <div class="chart-container">
          <h4>卖一量变化</h4>
          <div ref="sell1Chart" class="chart"></div>
        </div>
      </div>
      <div class="chart-row">
        <div class="chart-container">
          <h4>买一、买二挂单量 vs 成交量</h4>
          <div ref="buyCompareChart" class="chart"></div>
        </div>
        <div class="chart-container">
          <h4>卖一、卖二挂单量 vs 成交量</h4>
          <div ref="sellCompareChart" class="chart"></div>
        </div>
      </div>
      <div class="chart-row">
        <div class="chart-container">
          <h4>买盘深度变化</h4>
          <div ref="bidDepthChart" class="chart"></div>
        </div>
        <div class="chart-container">
          <h4>卖盘深度变化</h4>
          <div ref="askDepthChart" class="chart"></div>
        </div>
      </div>
      <div class="chart-row">
        <div class="chart-container wide">
          <h4>盘口深度气泡图 - 买盘</h4>
          <div ref="buyBubbleChart" class="chart"></div>
        </div>
        <div class="chart-container wide">
          <h4>盘口深度气泡图 - 卖盘</h4>
          <div ref="sellBubbleChart" class="chart"></div>
        </div>
      </div>
      <div class="chart-row">
        <div class="chart-container">
          <h4>累计成交量</h4>
          <div ref="cumulativeChart" class="chart"></div>
        </div>
      </div>
    </div>

    <div v-if="chartData.length > 0 && detections.length === 0" class="no-detection">
      <p>未检测到明显的冰山订单特征</p>
      <p class="hint">提示：冰山订单通常表现为某价位挂单量异常稳定且持续存在</p>
    </div>

    <!-- 使用说明 -->
    <div class="usage-guide">
      <h4>使用说明</h4>
      <ol>
        <li>首先访问 <a href="#/realtime-quote" target="_blank">实时行情页面</a>，输入股票代码并连接WebSocket</li>
        <li>等待3-5分钟让tick数据积累（数据每3秒更新一次）</li>
        <li>返回本页面，输入股票代码进行检测</li>
        <li>建议使用5-15分钟时间窗口获得更准确的检测结果</li>
      </ol>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'IcebergDetection',
  data() {
    return {
      stockCode: '603108',
      windowMinutes: 1440,  // 默认最近1天
      loading: false,
      errorMessage: '',
      insufficientDataWarning: null,
      detections: [],
      chartData: [],
      dataStatus: null,
      customStartTime: '',
      customEndTime: ''
    }
  },
  created() {
    // 将 charts 定义为非响应式属性，避免 Vue 深度观察 echarts 实例
    this.charts = []
  },
  beforeDestroy() {
    this.destroyCharts()
  },
  methods: {
    async checkDataStatus() {
      try {
        const response = await axios.get('/flow/data-status', {
          params: { code: this.stockCode }
        })
        this.dataStatus = response.data
      } catch (error) {
        console.error('检查数据状态失败:', error)
        // 尝试使用完整URL
        try {
          const res = await axios.get(`http://localhost:7350/flow/data-status?code=${this.stockCode}`)
          this.dataStatus = res.data
        } catch (err) {
          console.error('直接访问也失败:', err)
        }
      }
    },

    async handleDetect() {
      if (!this.stockCode) {
        this.errorMessage = '请输入股票代码'
        return
      }

      this.loading = true
      this.errorMessage = ''
      this.insufficientDataWarning = null
      this.destroyCharts()

      try {
        const params = {
          code: this.stockCode
        }

        // 处理自定义时间
        if (this.windowMinutes === -1) {
          if (!this.customStartTime || !this.customEndTime) {
            this.errorMessage = '请选择自定义的开始和结束时间'
            this.loading = false
            return
          }
          // 转换 datetime-local 格式 (YYYY-MM-DDTHH:mm) 为 API 需要的格式 (YYYY-MM-DD HH:mm:ss)
          const formatDateTime = (dt) => {
            // datetime-local 格式: "2026-03-20T01:09"
            // 需要转换为: "2026-03-20 01:09:00"
            const [date, time] = dt.split('T')
            const [hour, minute] = time.split(':')
            return `${date} ${hour}:${minute}:00`
          }
          params.start_time = formatDateTime(this.customStartTime)
          params.end_time = formatDateTime(this.customEndTime)
        } else {
          params.window_minutes = this.windowMinutes
        }

        let response
        try {
          response = await axios.get('/flow/iceberg-detect/day', { params })
        } catch (error) {
          console.warn('代理请求失败，尝试直接访问:', error)
          // 回退到直接访问后端（临时调试）
          response = await axios.get(`http://localhost:7350/flow/iceberg-detect/day?${new URLSearchParams(params)}`)
        }

        if (response.data.status === 'success') {
          this.detections = response.data.data || []
          this.chartData = response.data.chart_data || []

          if (this.chartData.length > 0) {
            this.$nextTick(() => {
              this.renderCharts()
            })
          }
        } else if (response.data.status === 'insufficient_data') {
          this.insufficientDataWarning = response.data
          this.detections = []
          this.chartData = []

          // 如果有可用数据的时间范围，自动填充自定义时间
          if (response.data.data_available && response.data.data_available.min_time) {
            const available = response.data.data_available
            this.customStartTime = available.min_time.replace(' ', 'T')
            this.customEndTime = available.max_time.replace(' ', 'T')
            this.windowMinutes = -1
          }
        } else {
          this.errorMessage = response.data.error || response.data.message || '检测失败'
        }
      } catch (error) {
        this.errorMessage = '请求失败: ' + (error.response?.data?.message || error.message || '网络错误')
      } finally {
        this.loading = false
      }
    },

    renderCharts() {
      this.destroyCharts()

      const times = this.chartData.map(d => d.timestamp)
      const sell1Volumes = this.chartData.map(d => d.sell1_volume)
      const buy1Volumes = this.chartData.map(d => d.buy1_volume)
      const volumeChanges = this.chartData.map(d => d.volume_change)

      // 图表1: 买一量变化
      const buy1Chart = echarts.init(this.$refs.buy1Chart)
      buy1Chart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { top: 20, right: 20, bottom: 50, left: 60 },
        xAxis: { type: 'category', data: times, axisLabel: { rotate: 45 } },
        yAxis: { type: 'value', name: '挂单量（手）' },
        series: [{
          name: '买一量',
          type: 'line',
          data: buy1Volumes,
          smooth: true,
          areaStyle: { color: 'rgba(82, 196, 26, 0.3)' },
          lineStyle: { color: 'rgb(82, 196, 26)', width: 2 },
          itemStyle: { color: 'rgb(82, 196, 26)' }
        }]
      })
      this.charts.push(buy1Chart)

      // 图表2: 卖一量变化
      const sell1Chart = echarts.init(this.$refs.sell1Chart)
      sell1Chart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { top: 20, right: 20, bottom: 50, left: 60 },
        xAxis: { type: 'category', data: times, axisLabel: { rotate: 45 } },
        yAxis: { type: 'value', name: '挂单量（手）' },
        series: [{
          name: '卖一量',
          type: 'line',
          data: sell1Volumes,
          smooth: true,
          areaStyle: { color: 'rgba(255, 77, 79, 0.3)' },
          lineStyle: { color: 'rgb(255, 77, 79)', width: 2 },
          itemStyle: { color: 'rgb(255, 77, 79)' }
        }]
      })
      this.charts.push(sell1Chart)

      // 图表3: 卖一、卖二挂单量 vs 成交量
      const sell2Volumes = this.chartData.map(d => d.sell2_volume)
      const sellCompareChart = echarts.init(this.$refs.sellCompareChart)
      sellCompareChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: {
          data: ['卖一挂单量', '卖二挂单量', '成交量变化'],
          top: 10,
          textStyle: { color: '#e2e8f0' },
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          borderColor: 'rgba(99, 102, 241, 0.3)',
          borderWidth: 1,
          borderRadius: 8,
          padding: [8, 16]
        },
        grid: { top: 50, right: 20, bottom: 50, left: 60 },
        xAxis: { type: 'category', data: times, axisLabel: { rotate: 45 } },
        yAxis: { type: 'value', name: '量（手）' },
        series: [
          {
            name: '卖一挂单量',
            type: 'bar',
            data: sell1Volumes,
            itemStyle: { color: 'rgba(255, 77, 79, 0.7)' }
          },
          {
            name: '卖二挂单量',
            type: 'bar',
            data: sell2Volumes,
            itemStyle: { color: 'rgba(255, 145, 158, 0.7)' }
          },
          {
            name: '成交量变化',
            type: 'bar',
            data: volumeChanges,
            itemStyle: { color: 'rgba(24, 144, 255, 0.7)' }
          }
        ]
      })
      this.charts.push(sellCompareChart)

      // 图表4: 买一、买二挂单量 vs 成交量
      const buy2Volumes = this.chartData.map(d => d.buy2_volume)
      const buyCompareChart = echarts.init(this.$refs.buyCompareChart)
      buyCompareChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: {
          data: ['买一挂单量', '买二挂单量', '成交量变化'],
          top: 10,
          textStyle: { color: '#e2e8f0' },
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          borderColor: 'rgba(99, 102, 241, 0.3)',
          borderWidth: 1,
          borderRadius: 8,
          padding: [8, 16]
        },
        grid: { top: 50, right: 20, bottom: 50, left: 60 },
        xAxis: { type: 'category', data: times, axisLabel: { rotate: 45 } },
        yAxis: { type: 'value', name: '量（手）' },
        series: [
          {
            name: '买一挂单量',
            type: 'bar',
            data: buy1Volumes,
            itemStyle: { color: 'rgba(82, 196, 26, 0.7)' }
          },
          {
            name: '买二挂单量',
            type: 'bar',
            data: buy2Volumes,
            itemStyle: { color: 'rgba(149, 222, 100, 0.7)' }
          },
          {
            name: '成交量变化',
            type: 'bar',
            data: volumeChanges,
            itemStyle: { color: 'rgba(24, 144, 255, 0.7)' }
          }
        ]
      })
      this.charts.push(buyCompareChart)

      // 图表5: 买盘深度变化
      const bidDepths = this.chartData.map(d => [
        d.buy1_volume, d.buy2_volume, d.buy3_volume, d.buy4_volume, d.buy5_volume
      ])
      const bidDepthChart = echarts.init(this.$refs.bidDepthChart)
      bidDepthChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: {
          data: ['买一', '买二', '买三', '买四', '买五'],
          top: 10,
          textStyle: { color: '#e2e8f0' },
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          borderColor: 'rgba(99, 102, 241, 0.3)',
          borderWidth: 1,
          borderRadius: 8,
          padding: [8, 16]
        },
        grid: { top: 50, right: 20, bottom: 50, left: 60 },
        xAxis: { type: 'category', data: times, axisLabel: { rotate: 45 } },
        yAxis: { type: 'value', name: '量（手）' },
        series: [
          { name: '买一', type: 'line', data: bidDepths.map(d => d[0]), smooth: true },
          { name: '买二', type: 'line', data: bidDepths.map(d => d[1]), smooth: true },
          { name: '买三', type: 'line', data: bidDepths.map(d => d[2]), smooth: true },
          { name: '买四', type: 'line', data: bidDepths.map(d => d[3]), smooth: true },
          { name: '买五', type: 'line', data: bidDepths.map(d => d[4]), smooth: true }
        ]
      })
      this.charts.push(bidDepthChart)

      // 图表6: 卖盘深度变化
      const askDepths = this.chartData.map(d => [
        d.sell1_volume, d.sell2_volume, d.sell3_volume, d.sell4_volume, d.sell5_volume
      ])
      const askDepthChart = echarts.init(this.$refs.askDepthChart)
      askDepthChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: {
          data: ['卖一', '卖二', '卖三', '卖四', '卖五'],
          top: 10,
          textStyle: { color: '#e2e8f0' },
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          borderColor: 'rgba(99, 102, 241, 0.3)',
          borderWidth: 1,
          borderRadius: 8,
          padding: [8, 16]
        },
        grid: { top: 50, right: 20, bottom: 50, left: 60 },
        xAxis: { type: 'category', data: times, axisLabel: { rotate: 45 } },
        yAxis: { type: 'value', name: '量（手）' },
        series: [
          { name: '卖一', type: 'line', data: askDepths.map(d => d[0]), smooth: true },
          { name: '卖二', type: 'line', data: askDepths.map(d => d[1]), smooth: true },
          { name: '卖三', type: 'line', data: askDepths.map(d => d[2]), smooth: true },
          { name: '卖四', type: 'line', data: askDepths.map(d => d[3]), smooth: true },
          { name: '卖五', type: 'line', data: askDepths.map(d => d[4]), smooth: true }
        ]
      })
      this.charts.push(askDepthChart)

      // 图表7: 盘口深度气泡图
      this.renderDepthBubbleCharts()
    },

    renderDepthBubbleCharts() {
      this.$nextTick(() => {
        if (!this.$refs.buyBubbleChart || !this.$refs.sellBubbleChart) {
          console.warn('bubble chart refs not available')
          return
        }

        // 断开响应式链接，使用纯数据
        const rawData = JSON.parse(JSON.stringify(this.chartData))
        const times = rawData.map(d => d.timestamp)

        // 冻结数组，防止 Vue 响应式观察
        Object.freeze(times)

        const buyLevelNames = ['买一', '买二', '买三', '买四', '买五']
        const sellLevelNames = ['卖一', '卖二', '卖三', '卖四', '卖五']

        // 预先计算颜色值
        const buyColors = ['#b7eb8f', '#95de64', '#73d13d', '#52c41a', '#389e0d']
        const sellColors = ['#ffccc7', '#ffa39e', '#ff7875', '#ff4d4f', '#cf1322']

        // 构建买盘数据 - 使用纯数组格式
        const buySeries = []
        for (let level = 1; level <= 5; level++) {
          const data = []
          for (let i = 0; i < rawData.length; i++) {
            const volume = rawData[i][`buy${level}_volume`]
            if (volume > 0) {
              data.push([times[i], volume])
            }
          }
          if (data.length > 0) {
            const seriesItem = {
              name: buyLevelNames[level - 1],
              type: 'scatter',
              data: data,
              symbolSize: 20,
              itemStyle: {
                color: buyColors[level - 1],
                opacity: 0.7
              }
            }
            Object.freeze(seriesItem)
            Object.freeze(seriesItem.itemStyle)
            buySeries.push(seriesItem)
          }
        }
        Object.freeze(buySeries)

        // 构建卖盘数据 - 使用纯数组格式
        const sellSeries = []
        for (let level = 1; level <= 5; level++) {
          const data = []
          for (let i = 0; i < rawData.length; i++) {
            const volume = rawData[i][`sell${level}_volume`]
            if (volume > 0) {
              data.push([times[i], volume])
            }
          }
          if (data.length > 0) {
            const seriesItem = {
              name: sellLevelNames[level - 1],
              type: 'scatter',
              data: data,
              symbolSize: 20,
              itemStyle: {
                color: sellColors[level - 1],
                opacity: 0.7
              }
            }
            Object.freeze(seriesItem)
            Object.freeze(seriesItem.itemStyle)
            sellSeries.push(seriesItem)
          }
        }
        Object.freeze(sellSeries)

        console.log('buySeries:', buySeries.length, 'sellSeries:', sellSeries.length)

        // 买盘气泡图
        const buyBubbleChart = echarts.init(this.$refs.buyBubbleChart)
        buyBubbleChart.setOption({
          tooltip: {
            trigger: 'item'
          },
          legend: {
            data: buySeries.map(s => s.name),
            top: 30,
            textStyle: { color: '#e2e8f0' },
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            borderColor: 'rgba(99, 102, 241, 0.3)',
            borderWidth: 1,
            borderRadius: 8,
            padding: [8, 16]
          },
          grid: {
            top: 50,
            right: 40,
            bottom: 80,
            left: 70
          },
          xAxis: {
            type: 'category',
            data: times,
            axisLabel: { rotate: 45 }
          },
          yAxis: {
            type: 'value',
            name: '挂单量（手）'
          },
          series: buySeries
        })
        this.charts.push(buyBubbleChart)

        // 卖盘气泡图
        const sellBubbleChart = echarts.init(this.$refs.sellBubbleChart)

        // 先设置一个简单的配置测试
        try {
          sellBubbleChart.setOption({
            grid: {
              top: 50,
              right: 40,
              bottom: 80,
              left: 70
            },
            xAxis: {
              type: 'category',
              data: times,
              axisLabel: { rotate: 45 }
            },
            yAxis: {
              type: 'value',
              name: '挂单量（手）'
            },
            series: sellSeries
          })
          this.charts.push(sellBubbleChart)
        } catch (error) {
          console.error('卖盘气泡图渲染失败:', error)
        }

        // 累计成交量图（放在最后）
        let cumulative = 0
        const cumulativeVolumes = this.chartData.map(d => {
          cumulative += d.volume_change
          return cumulative
        })
        const prices = this.chartData.map(d => d.price)
        const cumulativeChart = echarts.init(this.$refs.cumulativeChart)
        cumulativeChart.setOption({
          tooltip: { trigger: 'axis' },
          legend: {
            data: ['累计成交量', '实时价格'],
            top: 10,
            textStyle: { color: '#e2e8f0' },
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            borderColor: 'rgba(99, 102, 241, 0.3)',
            borderWidth: 1,
            borderRadius: 8,
            padding: [8, 16]
          },
          grid: { top: 50, right: 60, bottom: 50, left: 75 },
          xAxis: { type: 'category', data: times, axisLabel: { rotate: 45 } },
          yAxis: [
            {
              type: 'value',
              name: '累计成交量（手）',
              position: 'left',
              axisLabel: { color: '#52c41a' },
              splitLine: { show: true, interval: 3 }
            },
            {
              type: 'value',
              name: '价格（元）',
              position: 'right',
              axisLabel: { color: '#ff9800' },
              splitLine: { show: false }
            }
          ],
          series: [
            {
              name: '累计成交量',
              type: 'line',
              yAxisIndex: 0,
              data: cumulativeVolumes,
              smooth: true,
              areaStyle: { color: 'rgba(76, 175, 80, 0.3)' },
              lineStyle: { color: 'rgb(76, 175, 80)', width: 2 },
              itemStyle: { color: 'rgb(76, 175, 80)' }
            },
            {
              name: '实时价格',
              type: 'line',
              yAxisIndex: 1,
              data: prices,
              smooth: true,
              lineStyle: { color: '#ff9800', width: 2, type: 'dashed' },
              itemStyle: { color: '#ff9800' }
            }
          ]
        })
        this.charts.push(cumulativeChart)
      })
    },

    getBuyLevelColor(level) {
      const colors = ['#b7eb8f', '#95de64', '#73d13d', '#52c41a', '#389e0d']
      return colors[level] || '#52c41a'
    },

    getSellLevelColor(level) {
      const colors = ['#ffccc7', '#ffa39e', '#ff7875', '#ff4d4f', '#cf1322']
      return colors[level] || '#ff4d4f'
    },

    destroyCharts() {
      this.charts.forEach(chart => {
        if (chart) {
          chart.dispose()
        }
      })
      this.charts = []
    },

    getConfidenceClass(confidence) {
      if (confidence >= 70) return 'high'
      if (confidence >= 50) return 'medium'
      return 'low'
    }
  }
}
</script>

<style scoped>
.iceberg-detection {
  padding: 20px;
  width: 100%;
  background: transparent;
  color: #e2e8f0;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.header h2 {
  margin: 0;
  color: #e2e8f0;
  font-weight: 600;
}

.controls {
  display: flex;
  gap: 10px;
  align-items: center;
}

.stock-input {
  padding: 8px 12px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 4px;
  font-size: 14px;
  width: 150px;
  background: rgba(30, 41, 59, 0.8);
  color: #e2e8f0;
}

.time-select {
  padding: 8px 12px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 4px;
  font-size: 14px;
  background: rgba(30, 41, 59, 0.8);
  color: #e2e8f0;
}

.status-btn {
  padding: 8px 20px;
  background: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.status-btn:hover:not(:disabled) {
  background: rgba(99, 102, 241, 0.3);
  border-color: rgba(99, 102, 241, 0.5);
  color: #c4b5fd;
}

.status-btn:disabled {
  background: rgba(71, 85, 105, 0.5);
  border-color: rgba(71, 85, 105, 0.3);
  color: #64748b;
  cursor: not-allowed;
}

.time-input {
  padding: 8px 12px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 4px;
  font-size: 14px;
  background: rgba(30, 41, 59, 0.8);
  color: #e2e8f0;
}

.time-input::-webkit-calendar-picker-indicator {
  filter: invert(1);
}

.detect-btn {
  padding: 8px 20px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.detect-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
}

.detect-btn:disabled {
  background: #475569;
  cursor: not-allowed;
  box-shadow: none;
}

.error-message {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.results-section {
  margin-bottom: 30px;
}

.results-section h3 {
  color: #e2e8f0;
  margin-bottom: 15px;
}

.detection-table {
  overflow-x: auto;
}

.detection-table table {
  width: 100%;
  border-collapse: collapse;
}

.detection-table th,
.detection-table td {
  padding: 10px;
  text-align: center;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.detection-table th {
  background: rgba(30, 41, 59, 0.8);
  font-weight: 600;
  color: #e2e8f0;
}

.detection-table td.ask {
  color: #f87171;
}

.detection-table td.bid {
  color: #4ade80;
}

.detection-table td.high {
  color: #f87171;
  font-weight: 600;
}

.detection-table td.medium {
  color: #fbbf24;
  font-weight: 600;
}

.detection-table td.low {
  color: #4ade80;
}

.charts {
  margin-top: 30px;
}

.chart-row {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  width: 100%;
}

.chart-container {
  flex: 1;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 15px;
  min-height: 300px;
}

.chart-container.wide {
  flex: 2;
}

.chart-container h4 {
  margin: 0 0 10px 0;
  color: #e2e8f0;
  font-size: 14px;
  text-align: center;
}

.chart {
  width: 100%;
  height: 320px;
}

.no-detection {
  text-align: center;
  padding: 40px;
  color: #94a3b8;
}

.no-detection p {
  margin: 10px 0;
}

.no-detection .hint {
  font-size: 14px;
  color: #64748b;
}

.data-status {
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.data-status.has-data {
  background: rgba(34, 197, 94, 0.15);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #86efac;
}

.data-status.no-data {
  background: rgba(251, 191, 36, 0.15);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #fcd34d;
}

.data-status .detail {
  color: #94a3b8;
  font-size: 13px;
}

.data-status .hint {
  font-size: 13px;
  color: #64748b;
  margin-top: 5px;
}

.warning-message {
  background: rgba(251, 191, 36, 0.15);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #fcd34d;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.warning-message p {
  margin: 5px 0;
}

.warning-message .hint {
  font-size: 13px;
  color: #94a3b8;
}

.warning-message a {
  color: #a5b4fc;
  text-decoration: none;
}

.warning-message a:hover {
  text-decoration: underline;
}

.usage-guide {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 16px 20px;
  margin-top: 30px;
}

.usage-guide h4 {
  margin: 0 0 12px 0;
  color: #e2e8f0;
  font-size: 15px;
}

.usage-guide ol {
  margin: 0;
  padding-left: 20px;
  color: #94a3b8;
  line-height: 1.8;
}

.usage-guide li {
  margin-bottom: 6px;
}

.usage-guide a {
  color: #a5b4fc;
  text-decoration: none;
}

.usage-guide a:hover {
  text-decoration: underline;
}
</style>
